import React, { useEffect, useState } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { AudioProvider, useAudio } from "../Contexts/AudiosContext";
import AppRoutes from "./AppRoutes";
import Loading from "../Components/Loading";
import { LocaleProvider } from "../Contexts/LocaleContext";
import AudioPlayer from "../Components/AudioPlayer";
import { CookiesProvider } from "react-cookie";
import 'react-toastify/dist/ReactToastify.css';
import { VideoProvider } from "../Contexts/VideosContext";
import VideoDialog from "../Pages/VideoDialog";
import VideoDialogWrapper from "../Contexts/VideoDialogWrapper";
import InternetPage from "../Pages/NoInternetConnection";
import PageSeo from "./PageSeo";
import BooksService from "../Services/Books";
import GeetGovindServices from "../Services/GeetGovind";
import KalyansServices from "../Services/Kalyan";
import KalpataruServices from "../Services/Kalpataru";
import VivekService from "../Services/Vivekvani";
import ArticlesService from "../Services/Articles";
import AudioPlayerService from "../Services/AudioPlayer";
import { Type } from "react-toastify/dist/utils";
import LoginServices from "../Services/Login";
import UTMService from "../Services/UTMService";
import { buildUTMPayload, hasUTMParams, stripUTMFromSearch } from "../utils/utmUtils";
import MobileDevPageInfoBar from "../Pages/ResponsivePages/Common/MobileDevPageInfoBar";
import DesktopDevPageInfoBar from "../Pages/ResponsivePages/Common/DesktopDevPageInfoBar";
import MetaPixel from "../Components/MetaPixel";

// Browser-safe normalize helper used in auth checks
const normalize = (v?: string | null): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v);
  return (!s || s.trim() === "" || s === "null" || s === "undefined") ? null : s;
};

/////////////////////
// UTMHandler component
/////////////////////
function UTMHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip UTM tracking during react-snap prerender
    if ((window as any).__IS_SNAP_RENDER) return;

    if (!hasUTMParams(location.search)) return;

    const cleanSearch = stripUTMFromSearch(location.search);
    const cleanPath = location.pathname + cleanSearch;

    if (sessionStorage.getItem("utm_tracked")) {
      if (location.pathname + location.search !== cleanPath) {
        navigate(cleanPath, { replace: true });
      }
      return;
    }

    const payload = buildUTMPayload(location.search);
    sessionStorage.setItem("utm_tracked", "true");

    UTMService.track(payload).catch(() => {
      // Tracking must never break UX
    });

    navigate(cleanPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/////////////////////
// Canonical section keys (lowercase URL segment → API/route type)
/////////////////////
const SECTION_MAP: Record<string, string> = {
  books: "books",
  geetgovind: "geetgovind",
  kalyan: "kalyan",
  kalyanakalpataru: "kalyanakalpataru",
  vivekvani: "vivekvani",
  articles: "articles",
  audios: "audios",
  pravachans: "pravachans",
  dailysatsang: "DailySatsang",
  videos: "videos",
};

/////////////////////
// Navigation — deep-link state rebuild for detail URLs
/////////////////////
function Navigation({ onReady }: { onReady: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const isMongoId = (str: string) => /^[0-9a-fA-F]{24}$/.test(str);
  const parts = location.pathname.split("/").filter(Boolean);

  // Case-insensitive section parse: /Books/... and /Magazines/GeetGovind/... both work
  let rawType = parts[0] || "";
  let productType = parts[1] || "";

  if (rawType.toLowerCase() === "magazines" && parts.length >= 3) {
    rawType = parts[1] || "";
    productType = parts[2] || "";
  }

  const type = SECTION_MAP[rawType.toLowerCase()] || "";

  if (type === "DailySatsang" && parts.length > 2) {
    // Multi-segment DailySatsang paths keep the remainder as the item key
    productType = parts.slice(1).join("/");
  }

  useEffect(() => {
    let cancelled = false;
    const logdetails: string[] = [
      `pathname=${location.pathname}`,
      `rawType=${rawType}`,
      `type=${type || "(none)"}`,
      `productType=${productType || "(none)"}`,
    ];

    // If navigation already has state (i.e., user navigated inside app),
    // skip rebuild and mark ready immediately.
    if ((location.state && Object.keys(location.state).length > 0) || !type) {
      logdetails.push(type ? "skip=has_state" : "skip=unknown_section");
      console.log("[Navigation]", logdetails.join(" | "));
      setLoading(false);
      onReady();
      return;
    }

    // Skip language-only geetgovind segments (hindi/english) — list routes, not magazine slugs
    const productTypeLower = productType.toLowerCase();
    const isLangOnlyGeetGovind =
      type === "geetgovind" &&
      (productTypeLower === "hindi" ||
        productTypeLower === "english" ||
        productTypeLower === "hi" ||
        productTypeLower === "en");

    if (
      !productType ||
      productType === "undefined" ||
      productType === "null" ||
      isLangOnlyGeetGovind
    ) {
      logdetails.push(isLangOnlyGeetGovind ? "skip=lang_only_geetgovind" : "skip=no_productType");
      console.log("[Navigation]", logdetails.join(" | "));
      setLoading(false);
      onReady();
      return;
    }

    const run = async () => {
      try {
        // AUDIO / PRAVACHAN / DailySatsang
        if (type === "audios" || type === "pravachans" || type === "DailySatsang") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_audio identifier=${identifier}`);

          const response: any = await AudioPlayerService.getAudioById(identifier, type);

          if (cancelled) return;
          if (response?.status && response?.result) {
            // Keep product type in sync with URL so player/context do not reuse a
            // stale localStorage type from a previous section (e.g. DailySatsang).
            const prevType = localStorage.getItem("type");
            localStorage.setItem("type", type);
            logdetails.push(`set_type=${type}`);
            logdetails.push(`prev_type=${prevType || "none"}`);

            const canonicalPath = `/${type}/${response.result.slug}`;
            logdetails.push(`navigate=${canonicalPath}`);

            if (window.location.pathname !== canonicalPath || !location.state) {
              navigate(canonicalPath, {
                state: {
                  audioId: response.result.id,
                  audioslug: response.result.slug,
                  sorting: 0,
                  pageNum: 1,
                  index: 0,
                  audiocat: "",
                  audioAuthor: "",
                },
                replace: true,
              });
            }
          } else {
            logdetails.push(`audio_not_found redirect=/${type}`);
            navigate(`/${type}`, { replace: true });
          }

          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        // Books
        if (type === "books") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_book identifier=${identifier}`);
          const response: any = await BooksService.getBookDetail(identifier);
          if (cancelled) return;
          if (response?.result) {
            logdetails.push(`navigate=/books/${response.result.slug}`);
            navigate(`/books/${response.result.slug}`, {
              state: {
                bookId: response.result.id,
                bookName: response.result.name,
                bookSlug: response.result.slug,
                bookDetail: response.result,
              },
              replace: true,
            });
          } else {
            logdetails.push("book_not_found redirect=/books");
            navigate("/books", { replace: true });
          }
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        // GeetGovind (monthly magazine)
        if (type === "geetgovind") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_geetgovind identifier=${identifier}`);
          const response: any = await GeetGovindServices.getcurrentGeetGovind(identifier);
          if (cancelled) return;
          if (response?.result) {
            logdetails.push(`navigate=/magazines/geetgovind/${response.result.slug}`);
            navigate(`/magazines/geetgovind/${response.result.slug}`, {
              state: {
                MonthId: response.result.id,
                bookName: response.result.name,
                slug: response.result.slug,
              },
              replace: true,
            });
          } else {
            logdetails.push("geetgovind_not_found redirect=/magazines/geetgovind");
            navigate("/magazines/geetgovind", { replace: true });
          }
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        // Kalyan
        if (type === "kalyan") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_kalyan identifier=${identifier}`);
          const response: any = await KalyansServices.getcurrentKalyan(identifier);
          if (cancelled) return;
          if (response?.result) {
            logdetails.push(`navigate=/magazines/kalyan/${response.result.slug}`);
            navigate(`/magazines/kalyan/${response.result.slug}`, {
              state: {
                kalyanId: response.result.id,
                kalyansId: response.result.id,
                kalyanSlug: response.result.slug,
                bookName: response.result.name,
                slug: response.result.slug,
                isKalpataruBook: false,
                kalyanDetail: response.result,
              },
              replace: true,
            });
          } else {
            logdetails.push("kalyan_not_found redirect=/magazines/kalyan");
            navigate("/magazines/kalyan", { replace: true });
          }
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        // Kalyan Kalpataru
        if (type === "kalyanakalpataru") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_kalpataru identifier=${identifier}`);
          const response: any = await KalpataruServices.getcurrentKalpatarul(identifier);
          if (cancelled) return;
          if (response?.result) {
            logdetails.push(`navigate=/magazines/kalyanakalpataru/${response.result.slug}`);
            navigate(`/magazines/kalyanakalpataru/${response.result.slug}`, {
              state: {
                kalpatruId: response.result.id,
                kalpataruId: response.result.id,
                kalyansId: response.result.id,
                kalpatruSlug: response.result.slug,
                bookName: response.result.name,
                slug: response.result.slug,
                isKalpataruBook: true,
                kalpatruDetail: response.result,
              },
              replace: true,
            });
          } else {
            logdetails.push("kalpataru_not_found redirect=/magazines/kalyanakalpataru");
            navigate("/magazines/kalyanakalpataru", { replace: true });
          }
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        // Vivek Vani
        if (type === "vivekvani") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_vivekvani identifier=${identifier}`);
          const response: any = await VivekService.getVivekVaniDetail(identifier);
          if (cancelled) return;
          if (response?.result) {
            logdetails.push(`navigate=/magazines/vivekvani/${response.result.slug}`);
            navigate(`/magazines/vivekvani/${response.result.slug}`, {
              state: {
                vivekvaniId: response.result.id,
                vivekVaniId: response.result.id,
                vivekName: response.result.name,
                vivekSlug: response.result.slug,
                bookName: response.result.name,
                slug: response.result.slug,
                isVivekVaniBook: true,
                vivekvaniDetail: response.result,
              },
              replace: true,
            });
          } else {
            logdetails.push("vivekvani_not_found redirect=/magazines/vivekvani");
            navigate("/magazines/vivekvani", { replace: true });
          }
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        // Articles
        if (type === "articles") {
          const identifier = isMongoId(productType) ? productType : `slug-${productType}`;
          logdetails.push(`fetch_article identifier=${identifier}`);
          const response: any = await ArticlesService.getArticlesDetail(identifier);
          if (cancelled) return;
          if (response?.result) {
            logdetails.push(`navigate=/articles/${response.result.slug}`);
            navigate(`/articles/${response.result.slug}`, {
              state: {
                articleId: response.result.id,
                articleName: response.result.name,
                articleSlug: response.result.slug,
              },
              replace: true,
            });
          } else {
            logdetails.push("article_not_found redirect=/articles");
            navigate("/articles", { replace: true });
          }
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        if (type === "videos") {
          // Video route params are handled directly by VideoListPage.
          logdetails.push("skip=videos_passthrough");
          console.log("[Navigation]", logdetails.join(" | "));
          setLoading(false);
          onReady();
          return;
        }

        logdetails.push("skip=no_handler");
        console.log("[Navigation]", logdetails.join(" | "));
        setLoading(false);
        onReady();
      } catch (error) {
        logdetails.push(`error=${error}`);
        console.log("[Navigation]", logdetails.join(" | "));
        console.error("[Navigation] fetch error:", error);
        if (!cancelled) {
          setLoading(false);
          onReady();
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (loading) return <Loading />;

  return null;
}

/////////////////////
// Main App
/////////////////////
function App(props: any) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // navigationReady ensures Navigation finished its job (rebuild state / navigate)
  const [navigationReady, setNavigationReady] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const userId = normalize(localStorage.getItem("UserId") ?? "");
      const token = normalize(localStorage.getItem("Token") ?? "");

      console.log("=== App Start: Auth Check ===");
      if (userId && token && userId !== null && userId !== undefined && userId !== "") {
        try {
          console.log(`-> Refreshing token for user: ${userId}`);
          // Passing current token as fcmToken as found in legacy code pattern
          const response: any = await LoginServices.refreshTokenV2(userId, token);
          console.log("<- Refresh Response:", response);

          if (response && (response.status === true || response.status === 200) && response.result) {
            console.log("-> Token refreshed successfully.");
            localStorage.setItem("Token", response.result.token);
            // Ensure we update other keys if returned, or keep existing
            if (response.result.userId) localStorage.setItem("UserId", response.result.userId);
            if (response.result.signKey) localStorage.setItem("SignKey", response.result.signKey);

            // Update Image from response directly (no need for extra profile call)
            if (response.result.imagePath) {
              console.log("-> Updating 'Image' from refresh response.");
              localStorage.setItem("Image", response.result.imagePath);
            }

          } else {
            console.warn("-> Refresh failed or invalid response. Clearing auth.");
            clearAuthData();
          }
        } catch (e) {
          console.error("-> Error during token refresh:", e);
          clearAuthData();
        }
      } else {
        console.log("-> No user/token found in local storage. Proceeding as guest.");
      }
      setAuthChecked(true);
      console.log("=== App Start: Auth Check Complete ===");
    };

    const clearAuthData = () => {
      console.log("-> Clearing localStorage auth data.");
      const itemsToRemove = [
        "UserId", "Token", "SignKey", "UserName",
        "Image", "Phone", "Email", "fixedUserId", "signInMode"
      ];
      itemsToRemove.forEach(item => localStorage.removeItem(item));
    };

    checkAuthStatus();
  }, []);

  // onReady callback passed to Navigation
  const handleNavigationReady = () => setNavigationReady(true);

  return (
    isOnline ? (
      <LocaleProvider>
        <CookiesProvider>
          <React.Suspense fallback={<Loading />}>
            <BrowserRouter>
              <UTMHandler />
              {/* Meta Pixel initialization and pageview tracking */}
              <MetaPixel />
              {!authChecked ? (
                <Loading />
              ) : (
                <>
                  {/* Navigation runs first (inside router) and blocks while it rebuilds state */}
                  <Navigation onReady={handleNavigationReady} />

                  {/* Initialize providers and player only after navigationReady === true */}
                  {navigationReady ? (
                    <>
                      <PageSeo />
                      <VideoProvider>
                        <AudioProvider>
                          <AppRoutes />
                          {/* Dev-only: current route bar (mobile / desktop) */}
                          <MobileDevPageInfoBar />
                          <DesktopDevPageInfoBar />
                          <AudioPlayer />
                          <VideoDialogWrapper />
                        </AudioProvider>
                      </VideoProvider>
                    </>
                  ) : null}
                </>
              )}
            </BrowserRouter>
          </React.Suspense>
        </CookiesProvider>
      </LocaleProvider>
    ) : (
      <InternetPage />
    )
  );
}

export default App;
